import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { env } from "../../config/env";
import { MfaSettingsPanel } from "../auth/MfaSettingsPanel";
import { useAuth } from "../auth/useAuth";
import { useCompanyBranding } from "../../lib/useCompanyBranding";

type DashboardShellProps = {
  roleName: string;
  title: string;
  subtitle: string;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  lastUpdated: string;
  onRefresh: () => void;
  children: ReactNode;
  customizerPanel?: ReactNode;
  showCustomizer?: boolean;
  onToggleCustomize?: () => void;
  siteSwitcher?: ReactNode;
};

export function DashboardShell({
  roleName,
  title,
  subtitle,
  loading,
  refreshing,
  error,
  lastUpdated,
  onRefresh,
  children,
  customizerPanel,
  showCustomizer = false,
  onToggleCustomize,
  siteSwitcher,
}: DashboardShellProps) {
  const { session, logout, dashboardPath } = useAuth();
  const [showSecurity, setShowSecurity] = useState(false);
  const { logoUrl } = useCompanyBranding(session?.companyCode);

  return (
    <main className="app-shell">
      <section className="hero fade-in">
        <div className="hero-top">
          <div>
            <p className="eyebrow">
              {logoUrl ? (
                <img src={logoUrl} alt={session?.companyCode ?? "Company logo"} className="company-logo" />
              ) : (
                <span className="brand-wordmark">
                  0todeploy <span className="brand-vms">VMS</span>
                </span>
              )}
              <span className="role-chip">{roleName}</span>
            </p>
            <h1>{title}</h1>
            <p className="subtitle">{subtitle}</p>
          </div>
          <div className="session-tools">
            <span className="session-pill">{session?.username}</span>
            {customizerPanel && onToggleCustomize ? (
              <button className="secondary-btn" onClick={onToggleCustomize} type="button">
                Customize
              </button>
            ) : null}
            <button
              className="secondary-btn"
              onClick={() => setShowSecurity((prev) => !prev)}
              type="button"
            >
              Security
            </button>
            <button className="secondary-btn" onClick={logout} type="button">
              Sign out
            </button>
          </div>
        </div>
        <div className="meta-row">
          {siteSwitcher}
          {import.meta.env.DEV ? <span>API: {env.apiBaseUrl}</span> : null}
          <span>Refresh: {Math.round(env.dashboardRefreshMs / 1000)}s</span>
          <span>Updated: {lastUpdated}</span>
          <button
            className="refresh-btn"
            onClick={() => void onRefresh()}
            disabled={loading || refreshing}
            type="button"
          >
            {loading || refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>
        {error ? (
          <p className="error-banner">
            {error}{" "}
            <button className="inline-retry" onClick={() => void onRefresh()} type="button">
              Retry
            </button>
          </p>
        ) : null}
        {import.meta.env.DEV ? (
          <p className="route-note">
            Route: <Link to={dashboardPath}>{dashboardPath}</Link>
          </p>
        ) : null}
      </section>

      {showSecurity ? (
        <section className="panel-grid-full fade-in">
          <MfaSettingsPanel />
        </section>
      ) : null}

      {showCustomizer && customizerPanel ? (
        <section className="panel-grid-full fade-in">{customizerPanel}</section>
      ) : null}

      {children}
    </main>
  );
}
